/**
 * Enrollment agen telemetri — logika MURNI untuk kartu alat (ADR-0055 Fase 3).
 *
 * Backend-nya sudah ada: `POST/DELETE/GET /assets/:id/agent-token` (slice 1) dan jalur ingest
 * (slice 2). Berkas ini hanya menurunkan APA yang ditampilkan dari daftar token + perintah pasang
 * yang disalin operator — tak ada `fetch`, tak ada `import.meta.env`, supaya dapat diuji di Node.
 *
 * Satu token aktif per alat adalah invarian server (AgentTokenService.issue menolak yang kedua),
 * jadi `tokenAktif` mengembalikan paling banyak satu.
 */
import type { AgentTokenView } from '../api/types';
import { BATAS_BASI_MS } from './telemetry-health';

/**
 * Status agen sebagaimana TERBACA operator, bukan state mesin:
 * - `tidak-ada`      — belum ada token aktif; alat belum dipantau.
 * - `belum-terlihat` — token terbit tapi agen belum pernah mengirim (last_seen_at null).
 * - `aktif`          — agen mengirim dalam ambang kesegaran.
 * - `offline`        — agen pernah hidup tapi diam melewati ambang.
 */
export type StatusAgen = 'tidak-ada' | 'belum-terlihat' | 'aktif' | 'offline';

/** Token aktif alat (revoked_at null), atau null bila tak ada. */
export function tokenAktif(tokens: readonly AgentTokenView[]): AgentTokenView | null {
  return tokens.find((t) => t.revokedAt === null) ?? null;
}

/**
 * Status agen dari daftar token + waktu kini. Ambang basi default sama dengan deteksi offline
 * sisi server (`BATAS_BASI_MS`, 15 menit) supaya badge di web dan kejadian offline di server tidak
 * saling bertentangan. Waktu kini dioper (bukan `Date.now()` internal) agar deterministik di uji.
 */
export function statusAgen(
  tokens: readonly AgentTokenView[],
  now: Date,
  batasMs: number = BATAS_BASI_MS,
): StatusAgen {
  const aktif = tokenAktif(tokens);
  if (!aktif) return 'tidak-ada';
  if (aktif.lastSeenAt === null) return 'belum-terlihat';
  const seen = Date.parse(aktif.lastSeenAt);
  if (Number.isNaN(seen)) return 'belum-terlihat';
  return now.getTime() - seen > batasMs ? 'offline' : 'aktif';
}

export type AgenOS = 'windows' | 'linux';

/**
 * Nama biner rilis per OS. Windows = keluaran `make build-windows`
 * (`workforce-agent-windows-amd64.exe`). Linux memakai nama build native `make build`
 * (`workforce-agent`); operator yang memakai `make build-linux` memperoleh
 * `workforce-agent-linux-amd64` — jalankan dengan nama berkas yang benar-benar ada di PC itu.
 */
const BINER: Record<AgenOS, string> = {
  windows: 'workforce-agent-windows-amd64.exe',
  linux: 'workforce-agent',
};

/**
 * Perintah mulai-cepat yang disalin operator ke PC alat: set dua env var lalu jalankan biner.
 * Sepadan dengan kontrak agen (main.go): `WFA_API_URL` + `WFA_TOKEN`, biner dijalankan langsung
 * (TIDAK ada subperintah `install`). Untuk layanan permanen, runbook memakai systemd/nssm —
 * perintah ini untuk uji cepat bahwa token & jaringan benar.
 */
export function perintahPasang(apiUrl: string, token: string, os: AgenOS): string {
  if (os === 'windows') {
    return [
      `$env:WFA_API_URL="${apiUrl}"`,
      `$env:WFA_TOKEN="${token}"`,
      `.\\${BINER.windows}`,
    ].join('\n');
  }
  return [`export WFA_API_URL="${apiUrl}"`, `export WFA_TOKEN="${token}"`, `./${BINER.linux}`].join(
    '\n',
  );
}
